import React, { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import "./TestimonialsCarousel.scss";

const TestimonialsCarousel = ({
  testimonials,
  variant = "featured",
  autoplayMs = 6000,
}) => {
  const isHero = variant === "hero";
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    containScroll: "trimSnaps",
  });
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!emblaApi || reducedMotion || !autoplayMs) return;
    const timer = setInterval(() => emblaApi.scrollNext(), autoplayMs);
    return () => clearInterval(timer);
  }, [emblaApi, autoplayMs, reducedMotion]);

  useEffect(() => {
    if (!emblaApi || !isHero) return;

    const syncHeight = () => {
      requestAnimationFrame(() => {
        const viewport = emblaApi.rootNode();
        const slide = emblaApi.slideNodes()[emblaApi.selectedScrollSnap()];
        const content = slide?.querySelector(".testimonials__quote-block");
        if (viewport && content) {
          viewport.style.height = `${content.offsetHeight}px`;
        }
      });
    };

    syncHeight();
    emblaApi.on("select", syncHeight);
    emblaApi.on("reInit", syncHeight);
    emblaApi.on("resize", syncHeight);

    const images = emblaApi
      .rootNode()
      ?.querySelectorAll(".testimonials__avatar");
    images?.forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", syncHeight);
      }
    });

    return () => {
      emblaApi.off("select", syncHeight);
      emblaApi.off("reInit", syncHeight);
      emblaApi.off("resize", syncHeight);
      images?.forEach((img) => {
        img.removeEventListener("load", syncHeight);
      });
    };
  }, [emblaApi, isHero]);

  const slides = (
    <div
      className={`testimonials__viewport${
        isHero ? " testimonials__viewport--hero" : ""
      }`}
      ref={emblaRef}
    >
      <div className="testimonials__track">
        {testimonials.map((item, index) => (
          <div className="testimonials__slide" key={`${item.name}-${index}`}>
            <blockquote className="testimonials__quote-block">
              <p className="testimonials__quote">
                {isHero ? (
                  <span className="testimonials__quote-body">
                    <span className="testimonials__quote-mark" aria-hidden="true">
                      &ldquo;
                    </span>
                    {item.content}
                    <span className="testimonials__quote-mark" aria-hidden="true">
                      &rdquo;
                    </span>
                  </span>
                ) : (
                  item.content
                )}
              </p>
              <footer className="testimonials__author">
                <div className="testimonials__avatar-wrap">
                  <img
                    className="testimonials__avatar"
                    src={item.profilePic}
                    alt=""
                    width={isHero ? 50 : 44}
                    height={isHero ? 50 : 44}
                    loading="lazy"
                  />
                </div>
                <div className="testimonials__meta">
                  <cite className="testimonials__name">{item.name}</cite>
                  {!isHero && (
                    <span className="testimonials__role">Bitcoin Live member</span>
                  )}
                </div>
              </footer>
            </blockquote>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section
      className={`testimonials testimonials--${variant}`}
      aria-label={isHero ? "Member testimonials" : undefined}
      aria-labelledby={isHero ? undefined : "testimonials-heading"}
    >
      {!isHero && <div className="testimonials__glow" aria-hidden="true" />}

      {!isHero && (
        <header className="testimonials__header">
          <p className="testimonials__eyebrow">Community voices</p>
          <h2 id="testimonials-heading" className="testimonials__title">
            What members are saying
          </h2>
        </header>
      )}

      {isHero ? (
        <div className="testimonials__hero-inner">{slides}</div>
      ) : (
        <div className="testimonials__shell">
          <div className="testimonials__panel">{slides}</div>
        </div>
      )}
    </section>
  );
};

export default TestimonialsCarousel;
